import { Close, ContentCopy } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import CustomTooltip from "../../../../components/CustomTooltip";
import SpinningIcon from "../../../../components/SpinningIcon";
import { Toast } from "../../../../components/Toast";

const S3BucketConverter = ({ open, onClose, setShowS3BucketConverter }) => {
  const [accountIdInput, setAccountIdInput] = useState("");
  const [customerIdInput, setCustomerIdInput] = useState("");
  const [bucketNameResult, setBucketNameResult] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const colors = useColors();
  const { getBucketNameFromCustomerDetails } = useAPI();

  const handleConvertToBucket = async () => {
    if (!customerIdInput.trim() || !accountIdInput.trim()) {
      setError("Please enter a customer ID and account ID");
      return;
    }

    setIsLoading(true);
    setError("");
    setBucketNameResult("");

    try {
      const response = await getBucketNameFromCustomerDetails(
        accountIdInput.trim(),
        customerIdInput.trim()
      );
      const bucketName = response.data?.bucket_name;

      if (bucketName) {
        setBucketNameResult(bucketName);
        Toast.success("Successfully converted customer ID to bucket name");
      } else {
        setError("No bucket name found for this customer ID");
      }
    } catch (err) {
      console.error("Error converting customer ID to bucket name:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to convert customer ID to bucket name"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      Toast.success(`${type} copied to clipboard`);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      Toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        sx={{
          backgroundColor: colors.feasibility_high_bg,
          "&:hover": {
            backgroundColor: colors.feasibility_high_bg_hover,
          },
        }}
        onClick={() => setShowS3BucketConverter(true)}
      >
        S3 Bucket Converter
      </Button>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Customer ID → Bucket Name
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Box
              sx={{
                p: 2,
                mb: 2,
                backgroundColor: colors.error_background,
                borderRadius: 1,
                border: `1px solid ${colors.error_border}`,
              }}
            >
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Account ID"
              value={accountIdInput}
              onChange={(e) => setAccountIdInput(e.target.value)}
              placeholder="Enter account ID"
              size="small"
              sx={{ mb: 2 }}
              disabled={isLoading}
            />

            <TextField
              fullWidth
              label="Customer ID"
              value={customerIdInput}
              onChange={(e) => setCustomerIdInput(e.target.value)}
              placeholder="Enter customer ID"
              size="small"
              sx={{ mb: 2 }}
              disabled={isLoading}
            />

            <Button
              fullWidth
              variant="outlined"
              onClick={handleConvertToBucket}
              disabled={
                isLoading || !customerIdInput.trim() || !accountIdInput.trim()
              }
              startIcon={<SpinningIcon isLoading={isLoading} />}
              sx={{ mb: 2 }}
            >
              Convert to Bucket Name
            </Button>

            {bucketNameResult && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: colors.success_background,
                  borderRadius: 1,
                  border: `1px solid ${colors.success_border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Bucket Name:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                      fontWeight: 500,
                    }}
                  >
                    {bucketNameResult}
                  </Typography>
                </Box>
                <CustomTooltip title="Copy bucket name">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleCopyToClipboard(bucketNameResult, "Bucket name")
                    }
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </CustomTooltip>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default S3BucketConverter;
